import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useLoadOptions as useLoadOptionsHook } from "@/hooks/use-load-options";
import type { CRUDApiParams } from "@/lib/api-factory";

interface CRUDHooksConfig<T, TCreate, TUpdate, TParams extends CRUDApiParams> {
  queryKey: string;
  api: {
    getAll: (params?: TParams) => Promise<{ items: T[]; total: number; page: number; size: number; pages: number }>;
    create: (data: TCreate) => Promise<T>;
    update: (id: number, data: TUpdate) => Promise<T>;
    delete: (id: number) => Promise<void>;
  };
  invalidateKeys?: string[];
  mapToOption?: (item: T) => { value: any; label: string };
  getLoadParams?: (search: string, page: number, size: number) => TParams;
}

export function createCRUDHooks<T, TCreate, TUpdate, TParams extends CRUDApiParams>(
  config: CRUDHooksConfig<T, TCreate, TUpdate, TParams>
) {
  const useGetPaginated = (params: TParams = {} as TParams) => {
    return useQuery({
      queryKey: [config.queryKey, params],
      queryFn: () => config.api.getAll(params),
      placeholderData: keepPreviousData,
    });
  };

  const useCreate = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (data: TCreate) => config.api.create(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [config.queryKey] });
        config.invalidateKeys?.forEach(key => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      },
    });
  };

  const useUpdate = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: ({ id, data }: { id: number; data: TUpdate }) =>
        config.api.update(id, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [config.queryKey] });
        config.invalidateKeys?.forEach(key => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      },
    });
  };

  const useDelete = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (id: number) => config.api.delete(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      },
    });
  };

  const queryConfig = {
    queryKey: (params: TParams) => [config.queryKey, params] as const,
    queryFn: (params: TParams) => config.api.getAll(params),
  };

  const useLoadOptions = config.mapToOption && config.getLoadParams ? () => {
    return useLoadOptionsHook({
      queryConfig,
      mapToOption: config.mapToOption!,
      getParams: config.getLoadParams!,
      pageSize: 15,
    });
  } : undefined;

  return {
    useGetPaginated,
    useCreate,
    useUpdate,
    useDelete,
    queryConfig,
    useLoadOptions,
  };
}
